import React from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import ReactPlayer from "react-player";
import { CircularProgressCenter } from "@camberi/firecms";

import { PreviewImage } from "../../PreviewImage";
import OverscanOverlay from "./OverscanOverlay";
import MetadataViewer from "./MetadataViewer";

import { nonselect } from "../../../utils/basicFunctions";
import { isFreshSignedUrl, isHttpUrl } from "./playerUtils";

type PlayerStageProps = {
	assetNotLoadable: boolean;

	version: any;
	curPreviewAssetId: string;

	previewAssetIsVideo: boolean;
	previewAssetInfo: any;

	curVideoUrl: string;
	playerState: any;

	infoState: "none" | "compact" | "metadata";
	gridState: "none" | "grid" | "overscangrid";
	overlaySize: { width: number; height: number };
	versionMetadata: any;
	overscanPercentage: number;
	overscanZoom?: number;
	overscanZoomEnabled?: boolean;
	videoFlipped?: boolean;
};

type MemoizedReactPlayerProps = {
	playerRef: any;
	curVideoUrl: string;
	muted: boolean;
	playingValue: boolean;
	autoPlay: boolean;
	looping: boolean;
	seeking: boolean;
	setBuffering: (value: boolean) => void;
	play: () => void;
	pause: () => void;
	handleOnReady: () => void;
	handleProgress: () => void;
	handleEnded: () => void;
};

const reactPlayerConfig = {
	attributes: {
		disablePictureInPicture: true,
		controlsList: "nodownload",
		style: {
			width: "100%",
			height: "100%",
			objectFit: "contain",
			backgroundColor: "black",
			display: "block",
		},
	},
};

const MemoizedReactPlayer = React.memo(
	function MemoizedReactPlayer({ playerRef, curVideoUrl, muted, playingValue, autoPlay, looping, seeking, setBuffering, play, pause, handleOnReady, handleProgress, handleEnded }: MemoizedReactPlayerProps) {
		return (
			<ReactPlayer
				ref={playerRef}
				autoPlay={autoPlay}
				playsinline
				muted={muted}
				playing={playingValue}
				onBuffer={() => setBuffering(true)}
				onBufferEnd={() => setBuffering(false)}
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					width: "100%",
					height: "100%",
					outline: "none!important",
					opacity: 1,
					backgroundColor: "black",
				}}
				width="100%"
				height="100%"
				config={{ file: { ...reactPlayerConfig } }}
				url={curVideoUrl}
				onPlay={play}
				onPause={pause}
				onReady={handleOnReady}
				progressInterval={30}
				onProgress={handleProgress}
				loop={looping && !seeking}
				onEnded={handleEnded}
				onError={(e: any) => console.log(e)}
			/>
		);
	},
	(prev, next) => {
		return (
			prev.playerRef === next.playerRef &&
			prev.curVideoUrl === next.curVideoUrl &&
			prev.muted === next.muted &&
			prev.playingValue === next.playingValue &&
			prev.autoPlay === next.autoPlay &&
			prev.looping === next.looping &&
			prev.seeking === next.seeking &&
			prev.setBuffering === next.setBuffering &&
			prev.play === next.play &&
			prev.pause === next.pause &&
			prev.handleOnReady === next.handleOnReady &&
			prev.handleProgress === next.handleProgress &&
			prev.handleEnded === next.handleEnded
		);
	}
);

function getStrictFullOr2160ImageSource(previewAssetInfo: any) {
	return previewAssetInfo?.image2160Source || previewAssetInfo?.max2160Source || previewAssetInfo?.img2160Source || previewAssetInfo?.fullSource || null;
}

function getImageDisplayUrlFromSource(source: any) {
	if (!source) {
		return "";
	}

	if (isFreshSignedUrl(source.signedUrl)) {
		return source.signedUrl;
	}

	if (isHttpUrl(source.path)) {
		return source.path;
	}

	return "";
}

function PlayerStageComponent({
	assetNotLoadable,

	version,
	curPreviewAssetId,

	previewAssetIsVideo,
	previewAssetInfo,

	curVideoUrl,
	playerState,

	infoState,
	gridState,
	overlaySize,
	versionMetadata,
	overscanPercentage,
	overscanZoom = 1,
	overscanZoomEnabled = false,
	videoFlipped = false,
}: PlayerStageProps) {
	const {
		playerRef,
		playing,

		seeking,
		prefPlaying,

		looping,
		loopRangeActive,
		muted,
		buffering,
		setBuffering,

		play,
		pause,
		handleTogglePlayPause,
		handleProgress,
		handleOnReady,
		handleEnded,
	} = playerState;

	const mediaTop = 51;
	const mediaBottom = 196;
	const mediaHeight = `calc(100% - ${mediaBottom}px - ${mediaTop}px)`;

	const shouldZoomToNonOverscan = overscanZoomEnabled && overscanPercentage > 0;
	const safeOverscanZoom = shouldZoomToNonOverscan ? Math.max(overscanZoom || 1, 1) : 1;

	const hasMeasuredOverlaySize = overlaySize.width > 0 && overlaySize.height > 0;

	const mediaFrameWidth = hasMeasuredOverlaySize ? `${overlaySize.width}px` : "100%";
	const mediaFrameHeight = hasMeasuredOverlaySize ? `${overlaySize.height}px` : "100%";

	const strictImageSource = !previewAssetIsVideo ? getStrictFullOr2160ImageSource(previewAssetInfo) : null;
	const strictImageUrl = !previewAssetIsVideo ? getImageDisplayUrlFromSource(strictImageSource) : "";
	const shouldUseStrictImageRenderer = !previewAssetIsVideo && previewAssetInfo?.provider === "image";

	const viewportFlipTransform = videoFlipped ? "scaleX(-1)" : "none";

	const zoomBorder = shouldZoomToNonOverscan ? (
		<Box
			sx={{
				position: "absolute",
				inset: 0,
				border: "2px solid rgb(16, 185, 129)",
				boxSizing: "border-box",
				pointerEvents: "none",
				zIndex: 20,
			}}
		/>
	) : null;

	if (assetNotLoadable) {
		return (
			<Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
				<Box sx={{ width: 400, borderRadius: "25px", backgroundColor: "background.paper", overflow: "hidden" }}>
					<Box sx={{ height: 50, backgroundColor: "error.main", display: "flex", alignItems: "center", pl: 4 }}>
						<Typography variant="h6">{"Nothing to see here."}</Typography>
					</Box>

					<Box sx={{ pl: 4, pr: 4, pt: 3, mb: 3, display: "flex" }}>
						<Typography variant="body2">
							You can't review this version. It has been deleted or you are missing the permissions. <br />
							<br />
							Feel free to ask your project lead or admin for clarification.
						</Typography>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			{previewAssetIsVideo ? (
				<Box sx={{ ...nonselect, pt: "56.25%" }}>
					<Box
						data-tree-player-media-viewport="true"
						sx={{
							position: "absolute",
							top: mediaTop,
							left: 0,
							width: "100%",
							height: mediaHeight,
							backgroundColor: "black",
							overflow: "hidden",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							transform: viewportFlipTransform,
							transformOrigin: "center center",
						}}
					>
						<Box
							sx={{
								position: "relative",
								width: mediaFrameWidth,
								height: mediaFrameHeight,
								maxWidth: "100%",
								maxHeight: "100%",
								backgroundColor: "black",
								overflow: "hidden",
								flex: "0 0 auto",
								boxSizing: "border-box",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: "100%",
									backgroundColor: "black",
									overflow: "hidden",
									transform: `scale(${safeOverscanZoom})`,
									transformOrigin: "center center",
									zIndex: 1,
								}}
							>
								{curVideoUrl.length ? (
									<MemoizedReactPlayer
										playerRef={playerRef}
										curVideoUrl={curVideoUrl}
										muted={muted}
										playingValue={playing.current}
										autoPlay={prefPlaying}
										looping={looping && !loopRangeActive}
										seeking={seeking}
										setBuffering={setBuffering}
										play={play}
										pause={pause}
										handleOnReady={handleOnReady}
										handleProgress={handleProgress}
										handleEnded={handleEnded}
									/>
								) : null}
							</Box>

							{zoomBorder}
						</Box>
					</Box>

					{(infoState !== "none" || gridState !== "none") && (
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								transform: viewportFlipTransform,
								transformOrigin: "center center",
								pointerEvents: "none",
								zIndex: 30,
							}}
						>
							<OverscanOverlay
								infoState={infoState}
								gridState={gridState}
								overlaySize={overlaySize}
								version_metadata={versionMetadata}
								overscanPercentage={overscanPercentage}
								overscanZoomEnabled={overscanZoomEnabled}
								fio_asset_info={previewAssetInfo}
							/>
						</Box>
					)}

					<Box
						onClick={handleTogglePlayPause}
						sx={{
							position: "absolute",
							top: mediaTop,
							left: 0,
							width: "100%",
							height: mediaHeight,
							zIndex: 1000,
							display: "flex",
							flexDirection: "column",
							justifyContent: "flex-end",
						}}
					/>
				</Box>
			) : (
				<Box
					data-tree-player-media-viewport="true"
					sx={{
						position: "absolute",
						top: mediaTop,
						left: 0,
						width: "100%",
						height: mediaHeight,
						backgroundColor: "black",
						overflow: "hidden",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Box
						sx={{
							position: "relative",
							width: mediaFrameWidth,
							height: mediaFrameHeight,
							maxWidth: "100%",
							maxHeight: "100%",
							backgroundColor: "black",
							overflow: "hidden",
							flex: "0 0 auto",
							boxSizing: "border-box",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								backgroundColor: "black",
								overflow: "hidden",
								transform: `scale(${safeOverscanZoom})`,
								transformOrigin: "center center",
								zIndex: 1,
							}}
						>
							{shouldUseStrictImageRenderer ? (
								strictImageUrl.length ? (
									<Box
										component="img"
										key={`${curPreviewAssetId}_${strictImageSource?.pathField || strictImageUrl}`}
										src={strictImageUrl}
										sx={{
											position: "absolute",
											top: 0,
											left: 0,
											width: "100%",
											height: "100%",
											objectFit: "contain",
											backgroundColor: "black",
											display: "block",
										}}
									/>
								) : (
									<CircularProgressCenter />
								)
							) : (
								<PreviewImage
									key={curPreviewAssetId + "_preview_image"}
									version={version}
									assetId={curPreviewAssetId}
									autoplay={false}
									is_visible={true}
									img_type={"full"}
									img_fit_view={true}
									strict_asset_id={true}
									sx={{
										position: "absolute",
										top: 0,
										left: 0,
										width: "100%",
										height: "100%",
										backgroundColor: "black",
									}}
								/>
							)}
						</Box>

						{zoomBorder}
					</Box>
				</Box>
			)}

			{(!curVideoUrl.length || (buffering && prefPlaying)) && previewAssetIsVideo ? (
				<Box
					sx={{
						position: "absolute",
						top: mediaTop,
						left: 0,
						width: "100%",
						height: mediaHeight,
						backgroundColor: buffering ? "rgba(0,0,0,0)" : "black",
						zIndex: 1000,
					}}
				>
					<CircularProgressCenter />
				</Box>
			) : null}

			{infoState === "metadata" && versionMetadata && (
				<Box
					sx={{
						...nonselect,
						position: "absolute",
						top: mediaTop,
						left: 0,
						width: "100%",
						height: mediaHeight,
						zIndex: 9999,
						display: "flex",
						flexDirection: "column",
						justifyContent: "flex-end",
					}}
				>
					<Box
						id="metadata-view"
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							width: "100%",
							height: "100%",
							background: "rgba(0,0,0,0.9)",
							overflowY: "scroll",
						}}
					>
						<MetadataViewer data={{ version_metadata: versionMetadata }} expandDepth={25} style={{}} placeholder="Loading JSON..." />
					</Box>
				</Box>
			)}
		</Box>
	);
}

function playerStagePropsAreEqual(prev: PlayerStageProps, next: PlayerStageProps) {
	if (prev.assetNotLoadable !== next.assetNotLoadable) return false;
	if (prev.curPreviewAssetId !== next.curPreviewAssetId) return false;
	if (prev.previewAssetIsVideo !== next.previewAssetIsVideo) return false;
	if (prev.curVideoUrl !== next.curVideoUrl) return false;
	if (prev.infoState !== next.infoState) return false;
	if (prev.gridState !== next.gridState) return false;
	if (prev.overlaySize.width !== next.overlaySize.width) return false;
	if (prev.overlaySize.height !== next.overlaySize.height) return false;
	if (prev.overscanPercentage !== next.overscanPercentage) return false;
	if (prev.overscanZoom !== next.overscanZoom) return false;
	if (prev.overscanZoomEnabled !== next.overscanZoomEnabled) return false;
	if (prev.videoFlipped !== next.videoFlipped) return false;

	if (!prev.previewAssetIsVideo || !next.previewAssetIsVideo) {
		if (prev.version !== next.version) return false;
		if (prev.previewAssetInfo !== next.previewAssetInfo) return false;
	}

	if (prev.infoState !== "none" || prev.gridState !== "none" || next.infoState !== "none" || next.gridState !== "none") {
		if (prev.versionMetadata !== next.versionMetadata) return false;
		if (prev.previewAssetInfo !== next.previewAssetInfo) return false;
	}

	return (
		prev.playerState.playerRef === next.playerState.playerRef &&
		prev.playerState.playing === next.playerState.playing &&
		prev.playerState.seeking === next.playerState.seeking &&
		prev.playerState.prefPlaying === next.playerState.prefPlaying &&
		prev.playerState.looping === next.playerState.looping &&
		prev.playerState.loopRangeActive === next.playerState.loopRangeActive &&
		prev.playerState.muted === next.playerState.muted &&
		prev.playerState.buffering === next.playerState.buffering &&
		prev.playerState.setBuffering === next.playerState.setBuffering &&
		prev.playerState.play === next.playerState.play &&
		prev.playerState.pause === next.playerState.pause &&
		prev.playerState.handleTogglePlayPause === next.playerState.handleTogglePlayPause &&
		prev.playerState.handleProgress === next.playerState.handleProgress &&
		prev.playerState.handleOnReady === next.playerState.handleOnReady &&
		prev.playerState.handleEnded === next.playerState.handleEnded
	);
}

export const PlayerStage = React.memo(PlayerStageComponent, playerStagePropsAreEqual);
